import React from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { toast } from 'react-toastify';
import { SOURCE_LIST_MAX_HEIGHT } from '../constants';
import { formatFileSize, getFileExtension } from '../utils';
import { downloadProfileSourceFile } from '../../../utils/downloadProfileSource';
import SharedToggleButton from './SharedToggleButton';
import SourceProcessingStatus from './SourceProcessingStatus';
import SourceFailureNotice from './SourceFailureNotice';
import { isSourceFailed } from './sourceStatus';

const getFileIcon = (fileName, mimeType = '') => {
  const ext = getFileExtension(fileName);
  if (mimeType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return ImageOutlinedIcon;
  }
  if (ext === 'pdf' || mimeType === 'application/pdf') return PictureAsPdfOutlinedIcon;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return TableChartOutlinedIcon;
  if (['ppt', 'pptx'].includes(ext)) return SlideshowOutlinedIcon;
  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) return DescriptionOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
};

const DocumentList = ({
  documents = [],
  onRemove,
  removingId,
  showSharing = false,
  onToggleSharing,
  sharingId,
  onRetry,
  retryingId,
}) => {
  if (!documents.length) return null;

  const handleOpenFile = async (doc) => {
    try {
      await downloadProfileSourceFile(doc._id, doc.fileName);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to open this file');
    }
  };

  return (
    <Box
      sx={{
        maxHeight: SOURCE_LIST_MAX_HEIGHT,
        overflowY: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
      }}
    >
      {documents.map((doc) => {
        const Icon = getFileIcon(doc.fileName, doc.mimeType);
        return (
          <Box
            key={doc._id}
            px={1.25}
            py={1}
            borderBottom='1px solid'
            borderColor='divider'
            sx={{ '&:last-child': { borderBottom: 'none' } }}
          >
            <Box display='flex' alignItems='center' gap={1}>
              <Icon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
              <Box minWidth={0} flex={1} display='flex' flexDirection='column'>
                <Box minWidth={0} display='flex' alignItems='baseline' gap={1}>
                  <Link
                    component='button'
                    type='button'
                    underline='hover'
                    variant='body2'
                    noWrap
                    title={doc.fileName}
                    onClick={() => handleOpenFile(doc)}
                    sx={{ minWidth: 0, textAlign: 'left', cursor: 'pointer' }}
                  >
                    {doc.fileName}
                  </Link>
                  {doc.fileSize != null && (
                    <Typography variant='caption' color='primary.main' sx={{ flexShrink: 0 }}>
                      {formatFileSize(doc.fileSize)}
                    </Typography>
                  )}
                </Box>
                <SourceProcessingStatus source={doc} />
              </Box>
              {showSharing && (
                <SharedToggleButton
                  shared={!!doc.sharedWithLeadAgents}
                  disabled={sharingId === doc._id}
                  onToggle={() => onToggleSharing?.(doc)}
                />
              )}
              {onRemove && (
              <IconButton
                size='small'
                aria-label={`Remove ${doc.fileName}`}
                disabled={removingId === doc._id}
                onClick={() => onRemove(doc)}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
              )}
            </Box>

            {isSourceFailed(doc) && (
              <SourceFailureNotice
                source={doc}
                onRetry={onRetry}
                isRetrying={retryingId === doc._id}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default DocumentList;
